import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radar, RefreshCw, AlertTriangle, CalendarPlus, Users, Check, TrendingUp, Newspaper, Music2, Crosshair, ExternalLink } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";

const SOURCE_META = {
  google_trends: { label: "Google Trends", color: "#0B67FF", icon: TrendingUp },
  tiktok: { label: "TikTok", color: "#181621", icon: Music2 },
  news: { label: "News", color: "#E0A63C", icon: Newspaper },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

const tierMeta = (v) => (v >= 70 ? { label: "HOT", color: "#E0603A" } : v >= 40 ? { label: "WARM", color: "#E0A63C" } : { label: "COLD", color: "#0B67FF" });

const ScoreDial = ({ score, size = 54 }) => {
  const t = tierMeta(score);
  const r = 23;
  const c = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center shrink-0">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 56 56">
          <circle cx="28" cy="28" r={r} fill="none" stroke="var(--line)" strokeWidth="5" />
          <circle
            cx="28" cy="28" r={r} fill="none"
            stroke={t.color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)}
            transform="rotate(-90 28 28)"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[15px] font-extrabold text-[#181621] tabular-nums">{score}</span>
      </div>
      <span className="text-[9px] font-bold tracking-[0.1em] uppercase mt-1" style={{ color: t.color }}>{t.label}</span>
    </div>
  );
};

const SourceChip = ({ source }) => {
  const meta = SOURCE_META[source] || SOURCE_META.news;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.05em]" style={{ color: meta.color, borderColor: `${meta.color}40` }}>
      <Icon size={10} strokeWidth={2} /> {meta.label}
    </span>
  );
};

const TrendCard = ({ card, idx, onAddToPlan, onTestPanel, adding }) => (
  <div className="card-soft p-5 sm:p-6 flex flex-col" data-testid={`trend-card-${idx}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {card.virality != null && (
          <span data-testid={`trend-card-virality-${idx}`} title={card.virality_reason || ""}>
            <ScoreDial score={card.virality} />
          </span>
        )}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <SourceChip source={card.source} />
          {card.pillar && (
            <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-white px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.05em] text-[#54505B]" data-testid={`trend-card-pillar-${idx}`}>{card.pillar}</span>
          )}
        </div>
      </div>
      <span className="text-[11px] font-bold text-[#C9C6BE] tabular-nums shrink-0">#{idx + 1}</span>
    </div>
    <h3 className="font-bold text-[15.5px] text-[#181621] leading-snug mt-3" data-testid={`trend-card-title-${idx}`}>{card.title}</h3>
    {card.virality_reason && (
      <p className="text-[11.5px] text-[#9A9591] mt-1 leading-snug" data-testid={`trend-card-virality-reason-${idx}`}>{card.virality_reason}</p>
    )}
    {card.source_detail && (
      <p className="text-[11px] font-semibold text-[#0B67FF] mt-1.5 truncate flex items-center gap-1" title={card.source_detail}>
        <ExternalLink size={10} strokeWidth={2} className="shrink-0" /> {card.source_detail}
      </p>
    )}
    <div className="mt-4 flex-1">
      <div className="pb-3 border-b border-[#F3EFE7]">
        <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Why now</p>
        <p className="text-[12.5px] text-[#181621] leading-snug mt-1">{card.why_now}</p>
      </div>
      <div className="py-3 border-b border-[#F3EFE7]">
        <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">Why it matters for you</p>
        <p className="text-[12.5px] text-[#181621] leading-snug mt-1" data-testid={`trend-card-relevance-${idx}`}>{card.relevance}</p>
      </div>
      <div className="mt-3 rounded-r-lg border-l-[3px] border-l-[#E0603A] bg-[#FAF8F3] px-3.5 py-3">
        <p className="text-[9.5px] font-bold tracking-[0.1em] text-[#E0603A] uppercase flex items-center gap-1.5"><Crosshair size={10} strokeWidth={2} /> Possible application</p>
        <p className="text-[12.5px] text-[#181621] leading-snug mt-1 font-medium" data-testid={`trend-card-angle-${idx}`}>{card.angle}</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-4">
      {card.added_slot_id ? (
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#3E7B3E] py-1.5" data-testid={`trend-card-added-${idx}`}>
          <Check size={13} strokeWidth={2.5} /> In your plan
        </span>
      ) : (
        <SecondaryButton className="!py-1.5 !px-3 !text-[12px] flex items-center gap-1.5" onClick={() => onAddToPlan(card)} disabled={adding} data-testid={`trend-card-add-plan-${idx}`}>
          <CalendarPlus size={12} strokeWidth={1.8} /> {adding ? "Adding…" : "Add to editorial plan"}
        </SecondaryButton>
      )}
      <SecondaryButton className="!py-1.5 !px-3 !text-[12px] flex items-center gap-1.5" onClick={() => onTestPanel(card)} data-testid={`trend-card-test-panel-${idx}`}>
        <Users size={12} strokeWidth={1.8} /> Test on your audience
      </SecondaryButton>
    </div>
  </div>
);

export default function TrendRadar() {
  const [radar, setRadar] = useState(undefined);
  const [, setHasPillars] = useState(true);
  const [usage, setUsage] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | scanning | error
  const [stage, setStage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [addingCard, setAddingCard] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null); // null = all
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const stopPolling = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };

  const load = async () => {
    const { data } = await api.get("/trends");
    setRadar(data.radar);
    setHasPillars(data.has_pillars);
    setUsage(data.usage);
  };

  const startPolling = () => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 240) {
        stopPolling(); setPhase("error"); setErrMsg("The scan took too long. Try again.");
        return;
      }
      try {
        const { data } = await api.get("/trends/status");
        if (data.status === "failed") {
          stopPolling(); setPhase("error"); setErrMsg(data.error || "The scan failed. Try again.");
        } else if (data.status === "complete") {
          stopPolling(); setPhase("idle");
          await load();
          toast("Radar updated.", { description: "Fresh signals, curated for your business." });
        } else {
          setStage(data.stage || "");
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  useEffect(() => {
    load().catch(() => setRadar(null));
    api.get("/trends/status").then(({ data }) => {
      if (data.status === "pending") { setPhase("scanning"); setStage(data.stage || ""); startPolling(); }
    }).catch(() => {});
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scan = async () => {
    setErrMsg("");
    setPhase("scanning");
    setStage("Reading the radar…");
    try {
      await api.post("/trends/generate");
      startPolling();
    } catch (err) {
      setPhase("idle");
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addToPlan = async (card) => {
    setAddingCard(card.id);
    try {
      const { data } = await api.post("/trends/add-to-plan", { card_id: card.id });
      setRadar((r) => ({ ...r, cards: r.cards.map((c) => (c.id === card.id ? { ...c, added_slot_id: data.slot.id } : c)) }));
      toast("Added to your calendar.", { description: `Scheduled as an idea on ${fmtDate(data.slot.date)}.` });
      if (!data.has_pillars) {
        toast("Tip: confirm your content pillars to organize ideas by theme.", {
          action: { label: "Set up pillars", onClick: () => navigate("/app/organic/plan") },
        });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAddingCard(null);
    }
  };

  const testOnPanel = (card) => {
    navigate("/app/brainstorm/audience-test", { state: { prefill: `${card.title} — ${card.angle}` } });
  };

  const scansLeft = usage ? usage.limit - usage.used : null;
  const cards = radar?.cards || [];
  const scores = cards.map((c) => c.virality).filter((v) => v != null);
  const tierCounts = {
    hot: scores.filter((v) => v >= 70).length,
    warm: scores.filter((v) => v >= 40 && v < 70).length,
    cold: scores.filter((v) => v < 40).length,
  };
  const topScore = scores.length ? Math.max(...scores) : 0;
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
  const sourceCounts = Object.fromEntries(Object.keys(SOURCE_META).map((k) => [k, cards.filter((c) => c.source === k).length]));
  const sorted = [...cards].sort((a, b) => (b.virality ?? -1) - (a.virality ?? -1)).filter((c) => !sourceFilter || c.source === sourceFilter);

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="trend-radar-page">
      <MonoLabel>Brainstorming Area · Trend Radar</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        What's trending out there, filtered for your business
      </h1>
      <p className="mt-4 text-[12.5px] text-[#9A9591]" data-testid="trend-honest-label">
        Signals from Google Trends, TikTok and news — not research data · curated for your business.
      </p>

      {radar === undefined && <div className="h-24 mt-8 animate-pulse bg-[#F3EFE7] rounded-2xl" />}

      {phase === "scanning" && (
        <div className="card-soft mt-8 p-8 sm:p-10 flex flex-col items-center text-center" data-testid="trend-scanning-state">
          <div className="icon-tile animate-pulse" style={{ background: "var(--blue-bg)" }}>
            <Radar size={18} strokeWidth={1.8} style={{ color: "var(--blue-fg)" }} />
          </div>
          <p className="font-bold text-[15px] text-[#181621] mt-4" data-testid="trend-scanning-stage">{stage || "Scanning…"}</p>
          <p className="text-[12.5px] text-[#54505B] mt-1.5">First scan of the day pulls live data — it can take a few minutes. Repeat scans are quick.</p>
        </div>
      )}

      {phase === "error" && (
        <div className="card-soft mt-8 p-8 flex flex-col items-center text-center" data-testid="trend-error-state">
          <div className="icon-tile !w-12 !h-12" style={{ background: "var(--amber-bg)" }}>
            <AlertTriangle size={22} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
          </div>
          <p className="font-bold text-[15px] text-[#181621] mt-4">{errMsg}</p>
          <SecondaryButton className="!py-2 !px-4 !text-[13px] mt-4" onClick={scan} data-testid="trend-retry-btn">Try again</SecondaryButton>
        </div>
      )}

      {radar === null && phase === "idle" && (
        <div className="card-soft mt-8 p-10 sm:p-12 text-center relative overflow-hidden" data-testid="trend-empty-state">
          <div className="absolute left-1/2 top-16 -translate-x-1/2 pointer-events-none" aria-hidden="true">
            {[130, 220, 320].map((d) => (
              <span key={d} className="absolute rounded-full border border-[#EAE5DB]" style={{ width: d, height: d, left: -d / 2, top: -d / 2 }} />
            ))}
          </div>
          <div className="relative">
            <div
              className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
              style={{
                background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,.55), rgba(255,255,255,0) 72%), linear-gradient(135deg, #F1795A, #D24E2A)",
                boxShadow: "0 10px 24px -8px rgba(210,78,42,.5)",
              }}
            >
              <Radar size={26} strokeWidth={1.8} className="text-white" style={{ filter: "drop-shadow(0 2px 3px rgba(20,15,10,.25))" }} />
            </div>
            <p className="font-bold text-[16px] text-[#181621] mt-5">Your radar hasn't scanned yet.</p>
            <p className="text-[13px] text-[#54505B] mt-1.5 max-w-md mx-auto">
              We pull rising Google searches, TikTok trends and sector news for your market — then keep only what matters for your business, with a content angle ready to use.
            </p>
            <CtaButton className="mt-6" onClick={scan} data-testid="trend-scan-btn">
              <Radar size={14} strokeWidth={2} /> Scan the radar
            </CtaButton>
          </div>
        </div>
      )}

      {radar && phase === "idle" && (
        <div className="mt-8" data-testid="trend-results">
          <div className="card-soft p-6" data-testid="trend-radar-summary">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <p className="text-[30px] font-extrabold text-[#181621] tabular-nums leading-none">
                  {cards.length} <span className="text-[14px] font-bold text-[#54505B]">signals on your radar</span>
                </p>
                <p className="text-[12px] text-[#9A9591] mt-1.5">{radar.sector} · {radar.geo}</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {[["Top", topScore], ["Avg", avgScore]].map(([label, val]) => (
                  <div key={label} className="rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] px-4 py-2.5 text-center" data-testid={`trend-summary-${label.toLowerCase()}`}>
                    <p className="text-[10px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">{label}</p>
                    <p className="text-[17px] font-extrabold text-[#181621] tabular-nums">{val}</p>
                  </div>
                ))}
                <SecondaryButton className="!py-2 !px-3.5 !text-[12.5px] flex items-center gap-1.5" onClick={scan} data-testid="trend-refresh-btn"
                  title="Raw signals refresh once a day per sector — this re-curates them for your business.">
                  <RefreshCw size={12} strokeWidth={1.8} /> Re-curate
                </SecondaryButton>
              </div>
            </div>
            {scores.length > 0 && (
              <div className="mt-4" data-testid="trend-tier-distribution">
                <div className="flex h-1.5 rounded-full overflow-hidden bg-[#F3EFE7]">
                  {tierCounts.hot > 0 && <div style={{ flex: tierCounts.hot, background: "#E0603A" }} />}
                  {tierCounts.warm > 0 && <div style={{ flex: tierCounts.warm, background: "#E0A63C" }} />}
                  {tierCounts.cold > 0 && <div style={{ flex: tierCounts.cold, background: "#0B67FF" }} />}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {[["hot", "#E0603A"], ["warm", "#E0A63C"], ["cold", "#0B67FF"]].map(([tier, color]) => (
                    <span key={tier} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#54505B] tabular-nums">
                      <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {tierCounts[tier]} {tier}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-[#F3EFE7]">
              <div className="inline-flex rounded-xl border border-[#EAE5DB] bg-white overflow-hidden" data-testid="trend-sources-status">
                <button
                  onClick={() => setSourceFilter(null)}
                  className={`px-3.5 py-1.5 text-[11.5px] font-bold transition-colors ${!sourceFilter ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`}
                  data-testid="trend-source-all"
                >
                  All
                </button>
                {Object.entries(SOURCE_META).map(([key, meta]) => {
                  const st = radar.sources_status?.[key];
                  const count = sourceCounts[key];
                  return (
                    <button
                      key={key}
                      onClick={() => count > 0 && setSourceFilter(sourceFilter === key ? null : key)}
                      disabled={count === 0}
                      title={st === "failed" ? `${meta.label} — unavailable today` : st === "empty" ? `${meta.label} — no signals` : meta.label}
                      className={`px-3.5 py-1.5 text-[11.5px] font-bold border-l border-[#EAE5DB] transition-colors tabular-nums ${sourceFilter === key ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"} disabled:opacity-40 disabled:hover:bg-white`}
                      data-testid={`trend-source-${key}`}
                    >
                      {meta.label} {count > 0 && <span className={sourceFilter === key ? "text-white/60" : "text-[#9A9591]"}>{count}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select className="field-input !w-auto !py-1.5 !text-[12px]" defaultValue="score" data-testid="trend-sort-select">
                  <option value="score">Score · high to low</option>
                </select>
                <p className="text-[11.5px] text-[#9A9591] tabular-nums" data-testid="trend-freshness">
                  Signals fetched {fmtDate(radar.signals_fetched_at)} · curated {fmtDate(radar.curated_at)}
                </p>
                {usage && (
                  <p className={`text-[11.5px] font-semibold tabular-nums ${scansLeft > 1 ? "text-[#54505B]" : scansLeft > 0 ? "text-[#96691E]" : "text-[#B4574A]"}`} data-testid="trend-usage-counter">
                    {scansLeft} of {usage.limit} scans left today
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            {sorted.map((card, idx) => (
              <TrendCard key={card.id} card={card} idx={idx} onAddToPlan={addToPlan} onTestPanel={testOnPanel} adding={addingCard === card.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
